import { useCallback, useEffect, useMemo, useState, type FC } from "react";
import { useLocation } from "react-router-dom";

import { useCurrentView } from "@ds/hooks/use-current-view";
import { useVisibility } from "@ds/hooks/use-visibility";
import { useDeepRootSelector, useRootDispatch, useRootSelector } from "@ds/hooks";

import { EntityTypeName } from "@ds/model/entity-model";

import { getMainTableDataTypeByRoute, getPageTitleByTableDataType } from "@ds/modules/table-data/utils/helpers";
import { UploadFilesModal } from "@ds/modules/assets/components/forms/upload-files-modal";

import { MainView } from "@ds/components/main-view";
import { TableTabMenu } from "@ds/components/table/table-tab-menu";

import { AssetsTableActionsMenu } from "./components/common/assets-table-actions-menu";
import { AssetsTable } from "./components/assets-table";
import { AssetInfo } from "./components/asset-info";
import { AssetsGrid } from "./components/assets-grid";
import { UpdateAssetModal } from "./components/forms/update-asset-modal";
import { AssetsLayout } from "./components/types";
import { selectAggregatedAssetForSelectedEntities, selectAssetsIsSucceeded } from "./redux/selectors/common-selectors";
import { assetsActions, AssetsRemoteOperation } from "./redux/slice";
import { getApiContentTypeByTableType, getEntitySubtypeByTableType } from "./utils/helpers";

export const AssetsMainPage: FC = () => {
  const { pathname } = useLocation();

  const [currentView] = useCurrentView();
  const [isUpdateModalVisible, setIsUpdateModalVisible] = useVisibility();
  const isSucceeded = useRootSelector(state => selectAssetsIsSucceeded(state, AssetsRemoteOperation.THUMBNAILS));

  const filterOptions = useMemo(() => {
    const currentTableType = getMainTableDataTypeByRoute(pathname);
    return {
      entityType: EntityTypeName.ASSET,
      entitySubtype: getEntitySubtypeByTableType(currentTableType),
      tableType: currentTableType,
      queryFilters: { contentType: getApiContentTypeByTableType(currentTableType) },
    };
  }, [pathname]);

  const aggregatedAsset = useDeepRootSelector(state =>
    selectAggregatedAssetForSelectedEntities(state, filterOptions.tableType),
  );

  const [currentLayout, setCurrentLayout] = useState(AssetsLayout.LIST);

  useEffect(() => {
    if (isSucceeded) {
      setIsUpdateModalVisible(false)();
    }
  }, [isSucceeded, setIsUpdateModalVisible]);

  const onSwitchLayoutHandler = useCallback(
    (newLayout: AssetsLayout) => () =>
      setCurrentLayout(newLayout === AssetsLayout.TILES ? AssetsLayout.TILES : AssetsLayout.LIST),
    [setCurrentLayout],
  );

  const dispatch = useRootDispatch();
  const onUploadClickHandler = useCallback(
    (files: File[]) => {
      if (files.length) {
        dispatch(assetsActions.uploadAssets(files, filterOptions.tableType));
      }
    },
    [dispatch, filterOptions.tableType],
  );

  const setIsMinimized = useCallback(
    (value: boolean) => () => dispatch(assetsActions.setUploadModalMinimized(value)),
    [dispatch],
  );

  if (!currentView) {
    return null;
  }

  return (
    <>
      <MainView
        title={getPageTitleByTableDataType(filterOptions.tableType)}
        filterOptions={filterOptions}
        options={<TableTabMenu tableType={filterOptions.tableType} currentView={currentView} />}
        view={
          currentLayout === AssetsLayout.LIST ? (
            <AssetsTable tableType={filterOptions.tableType} onUpload={onUploadClickHandler} />
          ) : (
            <AssetsGrid tableType={filterOptions.tableType} onUpload={onUploadClickHandler} />
          )
        }
        info={<AssetInfo tableType={filterOptions.tableType} onUpload={onUploadClickHandler} />}
        actions={
          <AssetsTableActionsMenu
            filterOptions={filterOptions}
            layout={currentLayout}
            totalSelected={aggregatedAsset?.metadata?.totalAggregated || 0}
            onUpload={onUploadClickHandler}
            onUpdate={setIsUpdateModalVisible(true)}
            onSwitchLayout={onSwitchLayoutHandler}
          />
        }
      />

      <UploadFilesModal tableType={filterOptions.tableType} setIsMinimized={setIsMinimized} />

      {isUpdateModalVisible && aggregatedAsset && (
        <UpdateAssetModal
          tableType={filterOptions.tableType}
          initialValue={aggregatedAsset}
          onClose={setIsUpdateModalVisible(false)}
        />
      )}
    </>
  );
};
